/**
 * Active org selection context (A2F-244).
 *
 * Holds the currently selected org for users who belong to multiple orgs,
 * and keeps `localStorage` in sync so the API layer (lib/api.ts) can inject
 * the X-Org-Id header without a context hook.
 *
 * Selection model:
 *  - Seeded from `user.activeOrgId` (what the backend resolved this session into).
 *  - Overridable via `setActiveOrgId(id)` — writes localStorage + reloads the
 *    page so in-flight fetches and data caches are thrown away cleanly. The
 *    codebase has no unified query cache (no React Query / TanStack Query),
 *    so threading "reset" through every context (ProjectContext,
 *    DashboardCacheContext, ScanStatusContext, SubmissionFocusContext) would
 *    be substantial surface area for what is effectively a session change.
 *    Follow-up ticket would explore a softer refresh if the flash becomes
 *    a usability problem.
 *  - Cleared on logout (caller's responsibility) via `clearActiveOrg()`.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { ACTIVE_ORG_STORAGE_KEY, ORG_SWITCH_SSO_REQUIRED_EVENT } from './api';
import type { OrgMembership } from '@/types/api';

interface ActiveOrgContextValue {
  activeOrgId: string | null;
  orgs: OrgMembership[];
  activeOrg: OrgMembership | null;
  /** True iff a selection was present in localStorage at mount. Captured
   *  once so the full-page org picker gate (A2F-517) decides based on the
   *  user's durable choice, not the backend-resolved fallback. */
  hasStoredSelection: boolean;
  /** Set active org — writes localStorage and reloads the page. */
  setActiveOrgId: (orgId: string) => void;
  /** Clear any stored selection (logout). */
  clearActiveOrg: () => void;
}

const ActiveOrgContext = createContext<ActiveOrgContextValue>({
  activeOrgId: null,
  orgs: [],
  activeOrg: null,
  hasStoredSelection: false,
  setActiveOrgId: () => {},
  clearActiveOrg: () => {},
});

function readStored(): string | null {
  try {
    return localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(id: string | null) {
  try {
    if (id === null) {
      localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
    } else {
      localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, id);
    }
  } catch {
    /* ignore */
  }
}

export function ActiveOrgProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  const orgs = useMemo<OrgMembership[]>(() => user?.orgs ?? [], [user?.orgs]);

  const [activeOrgId, setActiveOrgIdState] = useState<string | null>(() => {
    const stored = readStored();
    if (stored) return stored;
    return user?.activeOrgId ?? user?.orgId ?? null;
  });

  // Captured once at mount so re-renders can't flip it. setActiveOrgId reloads
  // the page, so the next mount picks up the fresh value.
  const [hasStoredSelection] = useState<boolean>(() => readStored() !== null);

  // If the stored selection isn't in the user's orgs (e.g., membership
  // revoked), fall back to the backend-resolved active org.
  useEffect(() => {
    if (!user) return;
    const hasOrg = activeOrgId && orgs.some(o => o.orgId === activeOrgId);
    if (!hasOrg) {
      const fallback = user.activeOrgId ?? user.orgId ?? null;
      if (fallback !== activeOrgId) {
        writeStored(fallback);
        setActiveOrgIdState(fallback);
      }
    }
  }, [user, orgs, activeOrgId]);

  const setActiveOrgId = useCallback((orgId: string) => {
    writeStored(orgId);
    setActiveOrgIdState(orgId);
    // Hard reload — simplest path to refresh all contexts and in-flight
    // queries. See module docstring for rationale.
    window.location.reload();
  }, []);

  const clearActiveOrg = useCallback(() => {
    writeStored(null);
    setActiveOrgIdState(null);
  }, []);

  // When the api layer rejects an org due to SSO policy, api.ts clears the
  // stored selection and dispatches the event. Sync our state so the
  // selector reflects the fallback.
  useEffect(() => {
    const handler = () => {
      setActiveOrgIdState(user?.activeOrgId ?? user?.orgId ?? null);
    };
    window.addEventListener(ORG_SWITCH_SSO_REQUIRED_EVENT, handler);
    return () => window.removeEventListener(ORG_SWITCH_SSO_REQUIRED_EVENT, handler);
  }, [user?.activeOrgId, user?.orgId]);

  const activeOrg = useMemo<OrgMembership | null>(
    () => orgs.find(o => o.orgId === activeOrgId) ?? null,
    [orgs, activeOrgId],
  );

  const value = useMemo<ActiveOrgContextValue>(
    () => ({ activeOrgId, orgs, activeOrg, hasStoredSelection, setActiveOrgId, clearActiveOrg }),
    [activeOrgId, orgs, activeOrg, hasStoredSelection, setActiveOrgId, clearActiveOrg],
  );

  return (
    <ActiveOrgContext.Provider value={value}>
      {children}
    </ActiveOrgContext.Provider>
  );
}

export function useActiveOrg() {
  return useContext(ActiveOrgContext);
}
